import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'
import ReactCountryFlag from 'react-country-flag'
import { SUPPORTED_LANGUAGES } from '../utils/constants'

// Helper function to get country code for flag display
const getCountryCode = (languageCode) => {
  // Extract country code from locale (e.g., 'en-US' -> 'US', 'cs-CZ' -> 'CZ')
  if (languageCode.includes('-')) {
    return languageCode.split('-')[1]
  }
  // Handle legacy codes
  const legacyMap = { 'cs': 'CZ', 'sk': 'SK', 'de': 'DE', 'ru': 'RU', 'en': 'US' }
  return legacyMap[languageCode] || languageCode.toUpperCase()
}

// Helper function to get country name for accessibility
const getCountryName = (languageCode) => {
  const countryNames = {
    'AL': 'Albania', 'BA': 'Bosnia and Herzegovina', 'BG': 'Bulgaria',
    'HR': 'Croatia', 'CZ': 'Czech Republic', 'DK': 'Denmark',
    'NL': 'Netherlands', 'GB': 'United Kingdom', 'US': 'United States',
    'EE': 'Estonia', 'FI': 'Finland', 'FR': 'France',
    'DE': 'Germany', 'GR': 'Greece', 'HU': 'Hungary',
    'IS': 'Iceland', 'IT': 'Italy', 'LV': 'Latvia',
    'LT': 'Lithuania', 'MK': 'North Macedonia', 'NO': 'Norway',
    'PL': 'Poland', 'PT': 'Portugal', 'RO': 'Romania',
    'RU': 'Russia', 'SK': 'Slovakia', 'SI': 'Slovenia',
    'ES': 'Spain', 'SE': 'Sweden', 'UA': 'Ukraine'
  }
  const code = getCountryCode(languageCode)
  return countryNames[code] || code
}

// Languages that have UI locale files available
// This should match the locales imported in i18n/index.js
const AVAILABLE_UI_LANGUAGES = [
  'en',        // English (fallback)
  'sq-AL',     // Albanian
  'bs-BA',     // Bosnian
  'bg-BG',     // Bulgarian
  'hr-HR',     // Croatian
  'cs-CZ',     // Czech (maps to 'cs' for backward compatibility)
  'da-DK',     // Danish
  'nl-NL',     // Dutch
  'en-GB',     // English (UK)
  'en-US',     // English (US)
  'et-EE',     // Estonian
  'fi-FI',     // Finnish
  'fr-FR',     // French
  'de-DE',     // German (maps to 'de' for backward compatibility)
  'el-GR',     // Greek
  'hu-HU',     // Hungarian
  'is-IS',     // Icelandic
  'it-IT',     // Italian
  'lv-LV',     // Latvian
  'lt-LT',     // Lithuanian
  'mk-MK',     // Macedonian
  'no-NO',     // Norwegian
  'pl-PL',     // Polish
  'pt-PT',     // Portuguese
  'ro-RO',     // Romanian
  'ru-RU',     // Russian (maps to 'ru' for backward compatibility)
  'sk-SK',     // Slovak (maps to 'sk' for backward compatibility)
  'sl-SI',     // Slovenian
  'es-ES',     // Spanish
  'sv-SE',     // Swedish
  'uk-UA'      // Ukrainian
]

// Filter SUPPORTED_LANGUAGES to only include those with available UI translations
const UI_LANGUAGES = SUPPORTED_LANGUAGES.filter(lang => 
  AVAILABLE_UI_LANGUAGES.includes(lang.code)
)

// Add legacy short codes for backward compatibility
const LEGACY_MAPPINGS = [
  { code: 'cs', nameKey: 'lang.cs-CZ', flag: '🇨🇿' },
  { code: 'sk', nameKey: 'lang.sk-SK', flag: '🇸🇰' },
  { code: 'de', nameKey: 'lang.de-DE', flag: '🇩🇪' },
  { code: 'ru', nameKey: 'lang.ru-RU', flag: '🇷🇺' }
]

const ALL_UI_LANGUAGES = [...UI_LANGUAGES, ...LEGACY_MAPPINGS]

const LanguageSelector = ({ className = '' }) => {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  
  const currentLanguage = ALL_UI_LANGUAGES.find(lang => lang.code === i18n.language) || ALL_UI_LANGUAGES.find(lang => lang.code === 'en') || ALL_UI_LANGUAGES[0]

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode)
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <ReactCountryFlag 
            countryCode={getCountryCode(currentLanguage.code)}
            svg
            style={{ width: '20px', height: '15px', marginRight: '4px' }}
            title={`Flag of ${getCountryName(currentLanguage.code)}`}
            aria-label={`Flag of ${getCountryName(currentLanguage.code)}`}
          />
          <span>{t(currentLanguage.nameKey)}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 max-h-64 overflow-y-auto">
            {ALL_UI_LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  currentLanguage.code === language.code 
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <ReactCountryFlag 
                  countryCode={getCountryCode(language.code)}
                  svg
                  style={{ width: '20px', height: '15px' }}
                  title={`Flag of ${getCountryName(language.code)}`}
                  aria-label={`Flag of ${getCountryName(language.code)}`}
                />
                <span className="font-medium">{t(language.nameKey)}</span>
                {currentLanguage.code === language.code && (
                  <span className="ml-auto w-2 h-2 bg-primary-600 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default LanguageSelector